<template>
  <main class="booking-home">
    <!-- 返回首頁列 -->
    <div class="bh-topbar">
      <router-link to="/" class="bh-back-btn">← 返回首頁</router-link>
    </div>

    <section class="bh-hero">
      <h1>美容預約</h1>
      <p>找到適合您的美容店家，輕鬆線上預約</p>
      <router-link to="/posters/10306" class="bh-guide-link">第一次做臉？看怎麼挑店家、多久做一次 →</router-link>
    </section>

    <!-- 形象圖（同分享預覽圖） -->
    <div class="bh-banner">
      <img src="https://img.jiliskin.com/booking_og_1200x630.jpg" alt="美容預約：選地區、選服務、選時段，線上直接預約（做臉保養、美體 SPA、除毛、頭療采耳、霧眉美睫）" width="1200" height="630" fetchpriority="high">
    </div>

    <!-- 平台公告 -->
    <div v-if="announcements.length > 0" class="bh-announcements">
      <h2 class="bh-ann-title">📢 最新公告</h2>
      <div class="bh-ann-list">
        <div
          v-for="a in announcements.slice(0, 5)"
          :key="a.id"
          class="bh-ann-item"
          @click="router.push({ name: 'BookingAnnouncementDetail', params: { id: a.id } })"
        >
          <span class="bh-ann-badge">公告</span>
          <span class="bh-ann-name">{{ a.title }}</span>
          <span class="bh-ann-date">{{ annFormatDate(a.created_at) }}</span>
          <span class="bh-ann-arrow">→</span>
        </div>
      </div>
      <div v-if="announcements.length > 5" class="bh-ann-more">
        <button class="bh-ann-more-btn" @click="router.push({ name: 'BookingAnnouncements' })">
          查看更多公告 →
        </button>
      </div>
    </div>

    <!-- 篩選列 -->
    <section class="bh-filter">
      <div class="bh-filter__inner">
        <div class="bh-filter__group">
          <label for="filter-region">地區</label>
          <select id="filter-region" v-model="selectedRegion" @change="onFilter">
            <option value="">全部地區</option>
            <optgroup v-for="g in groupedRegions" :key="g.label" :label="g.label">
              <option v-for="r in g.regions" :key="r.id" :value="r.id">
                {{ r.name }}
              </option>
            </optgroup>
          </select>
        </div>

        <div class="bh-filter__group">
          <label for="filter-category">類型</label>
          <select id="filter-category" v-model="selectedCategory" @change="onFilter">
            <option value="">全部類型</option>
            <option v-for="c in bookingStore.categories" :key="c.id" :value="c.id">
              {{ c.name }}
            </option>
          </select>
        </div>

        <button class="bh-filter__reset" @click="onReset">清除篩選</button>
      </div>
    </section>

    <!-- 店家列表 -->
    <section class="bh-list">
      <!-- 載入中 -->
      <div v-if="bookingStore.storesLoading" class="bh-state">
        <span class="bh-spinner" />
        <p>載入中…</p>
      </div>

      <!-- 錯誤 -->
      <div v-else-if="bookingStore.storesError" class="bh-state bh-state--error">
        <p>{{ bookingStore.storesError }}</p>
        <button @click="loadStores">重新載入</button>
      </div>

      <!-- 空結果 -->
      <div v-else-if="bookingStore.stores.length === 0" class="bh-state">
        <p>目前沒有符合條件的店家</p>
      </div>

      <!-- 卡片列表 -->
      <div v-else class="bh-cards">
        <router-link
          v-for="store in bookingStore.stores"
          :key="store.id"
          :to="{ name: 'BookingStore', params: { id: store.id } }"
          class="bh-card"
          :class="{ 'bh-card--featured': store.is_featured }"
        >
          <!-- 推薦標籤 -->
          <div v-if="store.is_featured" class="bh-card__featured-badge">⭐ 推薦店家</div>

          <div class="bh-card__img">
            <img
              v-if="store.cover_image"
              :src="resolveImg(store.cover_image)"
              :alt="store.name"
            />
            <div v-else class="bh-card__img-placeholder" />
          </div>
          <div class="bh-card__body">
            <h2 class="bh-card__name">{{ store.name }}</h2>
            <span class="bh-card__mode" :class="{ 'bh-card__mode--line': !store.online_bookable }">
              {{ store.online_bookable ? '可線上預約' : '目前只開放 LINE 預約' }}
            </span>
            <p v-if="store.address" class="bh-card__address">{{ store.address }}</p>
            <p v-if="store.description" class="bh-card__desc">{{ store.description }}</p>
            <span class="bh-card__cta">查看詳情 →</span>
          </div>
        </router-link>
      </div>
    </section>

    <!-- 依地區找店家（各縣市地區頁：台南做臉推薦…） -->
    <section v-if="areas.length" class="bh-areas">
      <h2 class="bh-areas__title">依地區找店家</h2>
      <div class="bh-areas__list">
        <router-link v-for="a in areas" :key="a.slug" :to="`/booking/area/${a.slug}`" class="bh-areas__chip">
          {{ a.label }}美容預約（{{ a.count }}）
        </router-link>
      </div>
    </section>

    <!-- 店家招募：美業店家免費申請預約系統 -->
    <section class="bh-join">
      <h2 class="bh-join__title">你是美業店家嗎？</h2>
      <p class="bh-join__text">美容護膚、美甲、美睫、美髮、霧眉紋繡等店家，目前可免費申請線上預約系統，客人自己選時段預約，店家頁也有機會被搜尋到。</p>
      <div class="bh-join__btns">
        <router-link to="/store-apply" class="bh-join__btn">免費申請預約系統</router-link>
        <router-link to="/posters/10327" class="bh-join__link">看看有哪些功能 →</router-link>
      </div>
    </section>

    <BookingDisclaimer />

    <!-- 訪客查詢入口（懸浮按鈕） -->
    <BookingQueryFloat />
  </main>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useBookingStore } from '@/store/bookingStore'
import axios from 'axios'
import BookingQueryFloat from '@/components/BookingQueryFloat.vue'
import BookingDisclaimer from '@/components/BookingDisclaimer.vue'

const router           = useRouter()
const bookingStore     = useBookingStore()
const announcements    = ref([])
const areas            = ref([])
const selectedRegion   = ref('')
const selectedCategory = ref('')

function resolveImg(url) {
  if (!url) return ''
  if (url.startsWith('/api/booking/img/') && process.env.NODE_ENV === 'development') {
    return `https://jiliskin.com${url}`
  }
  return url.replace(/^http:\/\/127\.0\.0\.1:\d+\/uploads\//, '/uploads/')
}

function annFormatDate(dateStr) {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('zh-TW', {
    timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit'
  })
}

// 地區分組（按台灣地理區域）
const REGION_GROUPS = [
  { label: '北部', names: ['台北市', '新北市', '桃園市', '基隆市', '新竹市', '新竹縣', '宜蘭縣'] },
  { label: '中部', names: ['台中市', '苗栗縣', '彰化縣', '南投縣', '雲林縣'] },
  { label: '南部', names: ['台南市', '高雄市', '嘉義市', '嘉義縣', '屏東縣'] },
  { label: '東部', names: ['花蓮縣', '台東縣'] },
  { label: '離島', names: ['澎湖縣', '金門縣', '連江縣'] },
]

const groupedRegions = computed(() =>
  REGION_GROUPS.map(g => ({
    label: g.label,
    regions: g.names
      .map(name => bookingStore.regions.find(r => r.name === name))
      .filter(Boolean),
  })).filter(g => g.regions.length > 0)
)

onMounted(async () => {
  // regions / categories 有快取，重複呼叫不重撈
  await Promise.all([
    bookingStore.fetchRegions(),
    bookingStore.fetchCategories(),
  ])
  await loadStores()
  axios.get('/api/booking/areas').then(({ data }) => { areas.value = data.areas || [] }).catch(() => {})
  // 取得平台公告
  try {
    const { data } = await axios.get('/api/booking/announcements/active?frontend=1')
    announcements.value = data.announcements || []
  } catch (_) { /* 公告載入失敗靜默忽略 */ }
})

async function loadStores() {
  await bookingStore.fetchStores({
    regionId:   selectedRegion.value   || null,
    categoryId: selectedCategory.value || null,
  })
}

async function onFilter() {
  await loadStores()
}

async function onReset() {
  selectedRegion.value   = ''
  selectedCategory.value = ''
  await loadStores()
}
</script>

<style scoped>
.booking-home {
  max-width: 960px;
  margin: 0 auto;
  padding: 24px 16px 64px;
}

/* 返回首頁列 */
.bh-topbar {
  padding: 12px 20px 0;
}
.bh-back-btn {
  display: inline-flex;
  align-items: center;
  font-size: 0.9rem;
  color: #8b6f5c;
  text-decoration: none;
  font-weight: 600;
  transition: opacity 0.15s;
}
.bh-back-btn:hover { opacity: 0.7; }

/* 頁首 */
.bh-hero {
  text-align: center;
  padding: 40px 0 32px;
}
.bh-hero h1 {
  font-size: 2rem;
  margin-bottom: 8px;
  color: #333;
}
.bh-hero p {
  color: #666;
  font-size: 1rem;
}
.bh-guide-link {
  display: inline-block;
  margin-top: 10px;
  color: #a33b00;
  font-size: 0.95rem;
  font-weight: bold;
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* 依地區找店家 */
.bh-areas { max-width: 760px; margin: 32px auto 0; text-align: center; }
.bh-areas__title { font-size: 1.15rem; margin: 0 0 12px; color: #333; }
.bh-areas__list { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; }
.bh-areas__chip { padding: 6px 14px; border: 1px solid #e0d5cc; border-radius: 20px; color: #5a3e2b; text-decoration: none; font-size: 0.92rem; background: #faf7f4; }
.bh-areas__chip:hover { background: #f0e6dc; }

/* 店家招募 */
.bh-join {
  max-width: 760px;
  margin: 32px auto 8px;
  padding: 20px 18px;
  background: #fff8f0;
  border: 1px solid #f3d9c2;
  border-radius: 12px;
  text-align: center;
}
.bh-join__title { font-size: 1.15rem; margin: 0 0 8px; color: #333; }
.bh-join__text { font-size: 0.93rem; color: #555; line-height: 1.7; margin: 0 0 14px; }
.bh-join__btns { display: flex; flex-wrap: wrap; gap: 12px; justify-content: center; align-items: center; }
.bh-join__btn {
  display: inline-block;
  background: #ff6600;
  color: #fff;
  padding: 10px 24px;
  border-radius: 24px;
  font-weight: bold;
  text-decoration: none;
}
.bh-join__link { color: #a33b00; font-size: 0.93rem; font-weight: bold; text-decoration: underline; text-underline-offset: 3px; }

/* 篩選列 */
.bh-filter {
  background: #f9f6f3;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 32px;
}
.bh-filter__inner {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}
.bh-filter__group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bh-filter__group label {
  font-size: 0.85rem;
  color: #555;
}
.bh-filter__group select {
  font-size: 0.95rem;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  min-width: 140px;
}
.bh-filter__reset {
  font-size: 0.9rem;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #bbb;
  border-radius: 6px;
  cursor: pointer;
  color: #666;
  align-self: flex-end;
}
.bh-filter__reset:hover {
  background: #eee;
}

/* 狀態顯示 */
.bh-state {
  text-align: center;
  padding: 48px 0;
  color: #888;
}
.bh-state--error {
  color: #c0392b;
}
.bh-state button {
  margin-top: 12px;
  padding: 8px 20px;
  background: #8b6f5c;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
}
.bh-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid #ddd;
  border-top-color: #8b6f5c;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 12px;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* 卡片 */
.bh-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}
.bh-card {
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.09);
  text-decoration: none;
  color: inherit;
  background: #fff;
  transition: box-shadow 0.2s, transform 0.2s;
  position: relative;
}
.bh-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.14);
  transform: translateY(-2px);
}
/* 推薦店家 */
.bh-card--featured {
  border: 2px solid #f39c12;
  box-shadow: 0 4px 16px rgba(243,156,18,0.2);
}
.bh-card--featured:hover {
  box-shadow: 0 6px 24px rgba(243,156,18,0.35);
}
.bh-card__featured-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: linear-gradient(135deg, #f39c12, #e67e22);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  z-index: 1;
  letter-spacing: 0.03em;
}
.bh-card__img {
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background: #f0ebe6;
  flex-shrink: 0;
}
.bh-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
}
.bh-card__img-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f0ebe6 0%, #e0d5cc 100%);
}
.bh-card__body {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.bh-card__name {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}
.bh-card__mode {
  align-self: flex-start;
  font-size: 0.78rem; font-weight: 600;
  padding: 2px 10px; border-radius: 20px;
  color: #fff; background: #8b6f5c;
}
.bh-card__mode--line { color: #1b7a3d; background: #e6f6ec; }
.bh-card__address {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}
.bh-card__desc {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.bh-card__cta {
  margin-top: auto;
  font-size: 0.9rem;
  color: #8b6f5c;
  font-weight: 600;
}


.bh-banner { max-width: 900px; margin: 0 auto 28px; padding: 0 16px; }
.bh-banner img { display: block; width: 100%; height: auto; border-radius: 12px; box-shadow: 0 2px 10px rgba(0,0,0,0.08); }

.bh-announcements { max-width: 900px; margin: 0 auto 28px; padding: 0 16px; }
.bh-ann-title { font-size: 1.1rem; font-weight: 700; color: #2c3e50; margin: 0 0 14px; }

.bh-ann-list {
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
}
.bh-ann-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.15s;
}
.bh-ann-item:last-child { border-bottom: none; }
.bh-ann-item:hover { background: #fafafa; }

.bh-ann-badge {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 20px;
  background: #f59e0b;
  color: #fff;
  letter-spacing: 0.03em;
}
.bh-ann-name {
  flex: 1;
  font-size: 0.92rem;
  font-weight: 600;
  color: #2c3e50;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bh-ann-date { flex-shrink: 0; font-size: 0.78rem; color: #aaa; }
.bh-ann-arrow { flex-shrink: 0; font-size: 0.8rem; color: #bbb; }

.bh-ann-more {
  margin-top: 12px;
  text-align: right;
}
.bh-ann-more-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.88rem;
  color: #f59e0b;
  font-weight: 600;
  padding: 0;
  transition: opacity 0.15s;
}
.bh-ann-more-btn:hover { opacity: 0.7; }

.bh-guide { max-width: 900px; margin: 0 auto 20px; padding: 0 16px; }
.bh-guide__toggle {
  width: 100%; text-align: left; background: #f5f0ec;
  border: 1px solid #e0d5cc; border-radius: 8px; padding: 12px 16px;
  font-size: 0.95rem; font-weight: 600; color: #5a3e2b; cursor: pointer;
  list-style: none;
}
.bh-guide__toggle::-webkit-details-marker { display: none; }
.bh-guide__toggle::after { content: ' ▼'; font-size: 0.75rem; }
details[open] .bh-guide__toggle::after { content: ' ▲'; }
.bh-guide__content {
  background: #faf7f4; border: 1px solid #e0d5cc; border-top: none;
  border-radius: 0 0 8px 8px; padding: 16px 18px; font-size: 0.88rem;
  color: #555; line-height: 1.8;
}
.bh-guide__content p { margin: 0 0 8px; }
.bh-guide__section { font-weight: 700; color: #333; margin-top: 12px !important; }


@media (max-width: 780px) {
  .bh-cards { grid-template-columns: repeat(2, 1fr); gap: 14px; }
}

@media (max-width: 600px) {
  .bh-hero h1 { font-size: 1.5rem; }
  .bh-cards { grid-template-columns: repeat(2, 1fr); gap: 10px; }
}
</style>
